/**
 * The design tokens shared by the widget and the dashboard.
 *
 * There is one palette, one radius scale and one pair of font stacks, and both consumers read
 * them from here: `DEFAULT_PRESENTATION` below in `content.ts` (what a reader sees when a
 * publisher has not themed anything) and the Mantine theme in `apps/web/src/theme.ts` (what the
 * editor sees). That is the point of the file: the preview an editor looks at while theming a
 * brand has to match the widget by construction, not by two lists of hex codes staying in step.
 *
 * Nothing here imports anything, on purpose. The widget's bundle budget (see D7 in
 * docs/ARCHITECTURE.md) means it cannot pull zod in through the package barrel, so this module has
 * to stay importable on its own.
 *
 * Colour scales run light to dark, index 0 to 9, which is the shape Mantine expects for a colour.
 */

/** Shades of one colour, lightest first. */
export type ColourScale = readonly [
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
];

/**
 * A vivid violet, deliberately not a colour a newspaper uses.
 *
 * Publishers are red, blue or black. A tool that sits next to their newsroom software should not
 * look like it is impersonating the masthead, so the accent is somewhere no front page goes.
 */
export const VIOLET: ColourScale = [
  '#f2eefe',
  '#e2dbfc',
  '#c7b9f8',
  '#ab95f3',
  '#9377ef',
  '#7c60ea',
  '#6b4ee6',
  '#5b3fd1',
  '#4c34af',
  '#3d2a8d',
];

/** Warm coral, for highlights and the results a poll produces. */
export const CORAL: ColourScale = [
  '#fff1ed',
  '#ffe0d6',
  '#fcbfac',
  '#f99c80',
  '#f67d5a',
  '#f36741',
  '#f0552b',
  '#d64620',
  '#b3391a',
  '#8f2d14',
];

/** Neutrals with a little warmth in them, so nothing reads as cold SaaS blue-grey. */
export const WARM_GREY: ColourScale = [
  '#f8f5f1',
  '#efebe5',
  '#dfd9d1',
  '#cbc4ba',
  '#b2aaa0',
  '#948c82',
  '#756e66',
  '#5a544d',
  '#3f3a36',
  '#2a2621',
];

/**
 * The dark scheme's neutrals, same ordering: index 0 is the lightest (text), index 9 the deepest
 * (background). The hue carries a trace of the violet so dark mode does not turn grey.
 */
export const WARM_DARK: ColourScale = [
  '#ede9f2',
  '#d6d0de',
  '#aba3b8',
  '#837c90',
  '#5d566a',
  '#443e50',
  '#2f2a3a',
  '#1a1725',
  '#141220',
  '#0d0b14',
];

/** The named colours the two themes are built out of. */
export const BRAND = {
  /** Primary accent, the same shade in both colour schemes. */
  accent: VIOLET[6],
  /** Secondary, used for results and the occasional highlight. */
  highlight: CORAL[6],
  /** Warm near-black text on light backgrounds. */
  ink: WARM_DARK[7],
  /** Warm off-white page background. */
  ground: '#fbf9f5',
  /**
   * Cards, inputs and the widget's own background. Plain white: the widget sits inside somebody
   * else's article, where a tinted panel would read as a mistake rather than a choice.
   */
  surface: '#ffffff',
  /** Page background and card surface in dark mode. */
  groundDark: WARM_DARK[7],
  surfaceDark: WARM_DARK[6],
} as const;

/**
 * Corner radii in pixels, one notch friendlier than Mantine's defaults (2/4/8/16/32).
 *
 * `corner_radius` on a presentation is a number of pixels, which is why these are numbers rather
 * than rem strings; the Mantine theme converts them.
 */
export const RADIUS = {
  xs: 6,
  sm: 8,
  md: 12,
  lg: 18,
  xl: 26,
} as const;

/**
 * Type stacks.
 *
 * The display face carries the personality and is used for headings only. Body text is a system
 * stack: it is what an editor reads all day, it costs no download, and it is legible at 14px on
 * every machine a newsroom owns. Every stack ends in a real fallback, so a blocked font file
 * changes the character of a page and never its legibility.
 */
export const FONTS = {
  display: "'Bricolage Grotesque', 'Trebuchet MS', ui-sans-serif, system-ui, sans-serif",
  body: "ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  mono: "ui-monospace, SFMono-Regular, Menlo, Consolas, 'Liberation Mono', monospace",
} as const;
